import React from 'react';
import { Box, Typography, Divider, List, ListItem, ListItemText } from '@mui/material';
import { getColorLabel } from '../utils/colors';

interface OrderItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  size?: string;
  color?: string;
}

interface OrderSummaryProps {
  items: OrderItem[];
  promoLabel?: string;
  promoAmount?: number;
  shippingLabel?: string;
  shippingAmount?: number;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  items,
  promoLabel,
  promoAmount,
  shippingLabel,
  shippingAmount,
}) => {
  const subtotal = items.reduce(
    (sum: number, item: OrderItem) => sum + item.price * item.quantity,
    0
  );
  const discount = promoAmount && promoAmount > 0 ? promoAmount : 0;
  const shipping =
    typeof shippingAmount === 'number' && Number.isFinite(shippingAmount)
      ? shippingAmount
      : 0;
  const total = subtotal - discount + shipping;

  return (
    <Box sx={{ bgcolor: 'background.paper', p: 3, borderRadius: 1, boxShadow: 1 }}>
      <Typography variant="h6" gutterBottom>
        Récapitulatif de la commande
      </Typography>
      
      <List disablePadding>
        {items.map((item) => (
          <React.Fragment key={item.id}>
            <ListItem sx={{ py: 1, px: 0 }}>
              <ListItemText 
                primary={item.name} 
                secondary={`Quantité: ${item.quantity}` +
                  (item.size ? ` • Taille : ${item.size}` : '') +
                  (item.color ? ` • Couleur : ${getColorLabel(item.color)}` : '')}
              />
              <Typography variant="body2">
                {(item.price * item.quantity).toFixed(2)} €
              </Typography>
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
        {discount > 0 && (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemText primary={promoLabel || 'Code promo'} />
            <Typography variant="body2" color="error">
              -{discount.toFixed(2)} €
            </Typography>
          </ListItem>
        )}

        {shippingAmount != null && Number.isFinite(shippingAmount) && (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemText primary={shippingLabel || 'Frais de livraison'} />
            <Typography variant="body2">
              {shippingAmount.toFixed(2)} €
            </Typography>
          </ListItem>
        )}
        
        <ListItem sx={{ py: 2, px: 0 }}>
          <ListItemText primary="Total" />
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {total.toFixed(2)} €
          </Typography>
        </ListItem>
      </List>
    </Box>
  );
};

export default OrderSummary;
